import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BRAND_SOURCES, LOOK_COLOURS, brandRoles, contrast, endCard, isChromatic, markOn, mixColour, themeProposals,
  type BrandCandidate, type BrandChoice, type BrandView, type LookChange, type LookView, type ProfileResponse,
} from "@joogoos/contracts";
import { api, here } from "../api.js";
import { Icon } from "../ui/icons.js";

/** The colours a video is made with when the business has none (`compose.ts`). The preview shows the same. */
const DEFAULT_COLOURS = ["#1f2937", "#d97706", "#fef3c7"];

const thumb = (id: string) => here(`/v1/workspace/brand/${id}/thumb`);

/**
 * Your look, the second view on Business (ADR 0029, 03 §your look): what every post and video is made with, shown as
 * they will be made with it, and the one place to choose or change it.
 *
 * It has two moods. While a lookup waits for an answer it is a choice: the pictures, logos and colours found, opened
 * chosen the way a quick yes should go, and *Use this look* or *None of these*. The rest of the time it is the look as
 * it stands, and changing it is free: another logo, no logo, another theme. The preview is the point either way — a
 * narrated video's caption over a photo and its last frame, drawn by the roles the renderer reads the colours in, so a
 * theme is judged on what it will look like rather than on two swatches.
 */
export function LookPanel({ look, brand, name, onChoose, choosing, chooseError }: {
  look: LookView;
  brand: BrandView | null;
  name: string;
  onChoose: (choice: BrandChoice) => void;
  choosing: boolean;
  chooseError?: string | undefined;
}) {
  return brand
    ? <Deciding key={brand.candidates[0]?.id} brand={brand} name={name} line={look.endLine} onChoose={onChoose} choosing={choosing} error={chooseError} />
    : <Standing look={look} name={name} />;
}

/** A lookup found things, and waits for the owner to say which to keep. */
function Deciding({ brand, name, line, onChoose, choosing, error }: {
  brand: BrandView; name: string; line: string; onChoose: (choice: BrandChoice) => void; choosing: boolean; error?: string | undefined;
}) {
  const photos = brand.candidates.filter((c) => !c.logo);
  const logos = brand.candidates.filter((c) => c.logo);
  const [keep, setKeep] = useState<string[]>(() => photos.filter((c) => c.source !== "web").map((c) => c.id));
  const [logo, setLogo] = useState<string | null>(brand.logo);
  const themesFor = (id: string | null) => themeProposals({ current: [], logo: logos.find((c) => c.id === id)?.colours ?? [], site: brand.siteColours });
  const [colours, setColours] = useState<string[]>(() => [...(themesFor(brand.logo)[0]?.colours ?? [])]);
  const themes = themesFor(logo);
  const pickLogo = (id: string | null) => { setLogo(id); setColours([...(themesFor(id)[0]?.colours ?? colours)]); };
  const toggle = (id: string) => setKeep((k) => (k.includes(id) ? k.filter((x) => x !== id) : [...k, id]));
  const firstKept = photos.find((c) => keep.includes(c.id));

  return (
    <section aria-label="Choose what to keep" className="bg-panel border border-line rounded-[10px] px-4 py-3.5 flex flex-col gap-3.5">
      <Heading note="found by the lookup · nothing is kept until you say" />
      <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] gap-4">
        <Preview colours={colours} logoUrl={logo ? thumb(logo) : null} photoUrl={firstKept ? thumb(firstKept.id) : null} name={name} line={line} />
        <div className="flex flex-col gap-3.5">
          {logos.length ? (
            <Group label="Logo">
              <div className="flex flex-wrap gap-1.5">
                {logos.map((c) => (
                  <button key={c.id} onClick={() => pickLogo(c.id)} aria-pressed={logo === c.id} aria-label={`Logo from ${c.from}`}
                    className={`w-20 h-14 rounded-lg border-2 bg-white p-1.5 grid place-items-center ${logo === c.id ? "border-coral" : "border-line hover:border-line-2"}`}>
                    <img src={thumb(c.id)} alt="" className="max-w-full max-h-full object-contain" />
                  </button>
                ))}
                <button onClick={() => pickLogo(null)} aria-pressed={logo === null}
                  className={`w-20 h-14 rounded-lg border-2 text-[11.5px] text-muted ${logo === null ? "border-coral" : "border-line hover:border-line-2"}`}>
                  No logo
                </button>
              </div>
            </Group>
          ) : null}
          <Themes themes={themes} colours={colours} onChange={setColours} />
        </div>
      </div>

      {photos.length ? (
        <div className="border-t border-line pt-3 flex flex-col gap-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted flex">
            Pictures<span className="ml-auto normal-case tracking-normal font-normal">{keep.length} of {photos.length} kept</span>
          </h3>
          {BRAND_SOURCES.map(([source, label]) => {
            const group = photos.filter((c) => c.source === source);
            return group.length ? <Pictures key={source} label={label} group={group} keep={keep} onToggle={toggle} /> : null;
          })}
          {photos.some((c) => c.source === "web") ? <p className="text-[11.5px] text-muted">Keep only pictures that are yours to use.</p> : null}
        </div>
      ) : null}

      {error ? <p className="text-bad text-xs">{error}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => onChoose({ keep, logo, colours })} disabled={choosing} className="px-3.5 py-2 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40">
          {choosing ? "Keeping…" : "Use this look"}
        </button>
        <button onClick={() => onChoose({ keep: [], logo: null, colours: [] })} disabled={choosing} className="px-3.5 py-2 rounded-lg border border-line-2 bg-panel text-xs font-medium hover:bg-page">
          None of these
        </button>
      </div>
    </section>
  );
}

/** The look as it stands, and the way to change it. Free. */
function Standing({ look, name }: { look: LookView; name: string }) {
  const qc = useQueryClient();
  const [colours, setColours] = useState<string[]>(look.palette);
  const [shown, setShown] = useState(look.palette.join());
  // A save or an upload elsewhere changes the look under this panel; what is being tried here follows it.
  if (shown !== look.palette.join()) { setShown(look.palette.join()); setColours(look.palette); }
  const file = useRef<HTMLInputElement>(null);
  const done = (r: ProfileResponse) => qc.setQueryData(["profile"], r);
  const upload = useMutation({
    mutationFn: (f: File) => api.uploadLogo(f),
    onSuccess: (r) => {
      done(r);
      // A business with no colours yet starts from its logo's; the owner saves them or not.
      if (!look.palette.length) setColours([...(themeProposals({ current: [], logo: r.logoColours, site: look.siteColours })[0]?.colours ?? [])]);
    },
  });
  const change = useMutation({ mutationFn: (c: LookChange) => api.setLook(c), onSuccess: done });
  const media = useQuery({ queryKey: ["media", "image"], queryFn: () => api.media("image"), staleTime: 60_000 });
  const photo = (media.data ?? []).find((m) => m.origin !== "generated" && !m.tags.includes("logo"));
  const themes = themeProposals({ current: look.palette, logo: look.logoColours, site: look.siteColours });
  const changed = colours.join() !== look.palette.join();
  const error = upload.error?.message ?? change.error?.message;

  return (
    <section aria-label="Your look" className="bg-panel border border-line rounded-[10px] px-4 py-3.5 flex flex-col gap-3.5">
      <Heading note={look.palette.length || look.logoUrl ? "how your posts and videos look" : "no logo or colours yet · pick a theme to start"} />
      <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] gap-4">
        <Preview colours={colours} logoUrl={look.logoUrl} photoUrl={photo?.url ?? null} name={name} line={look.endLine} />
        <div className="flex flex-col gap-3.5">
          <Group label="Logo">
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-20 h-14 rounded-lg border border-line bg-white p-1.5 grid place-items-center text-[11.5px] text-faint">
                {look.logoUrl ? <img src={look.logoUrl} alt="Your logo" className="max-w-full max-h-full object-contain" /> : "No logo"}
              </span>
              <button onClick={() => file.current?.click()} disabled={upload.isPending} className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium hover:bg-page disabled:opacity-40 flex items-center gap-1.5">
                <Icon name="plus" size={12} /> {upload.isPending ? "Uploading…" : "Upload a logo"}
              </button>
              {look.logoUrl ? <button onClick={() => change.mutate({ logo: null })} className="px-2 py-1.5 text-xs text-muted hover:text-ink">Remove the logo</button> : null}
              <input ref={file} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg" hidden aria-label="Logo file"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }} />
            </div>
          </Group>
          <Themes themes={themes} colours={colours} onChange={setColours} />
          {changed ? (
            <div className="flex items-center gap-2">
              <button onClick={() => change.mutate({ palette: colours })} disabled={change.isPending} className="px-3.5 py-2 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40">
                {change.isPending ? "Saving…" : "Save these colours"}
              </button>
              <button onClick={() => setColours(look.palette)} className="px-3 py-2 text-xs text-muted hover:text-ink">Put them back</button>
            </div>
          ) : null}
          {error ? <p className="text-bad text-xs">{error}</p> : null}
        </div>
      </div>
    </section>
  );
}

function Heading({ note }: { note: string }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="shrink-0 whitespace-nowrap text-[11px] font-semibold uppercase tracking-[.06em] text-muted">Your look</span>
      <span className="text-[11.5px] text-muted">{note}</span>
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className="text-[11px] font-semibold uppercase tracking-[.06em] text-muted">{label}</h3>
      {children}
    </div>
  );
}

/**
 * A narrated video's caption over a photo, and its last frame, drawn the way `JoogoosVideo.tsx` draws them, from the same
 * roles (`brandRoles`, ADR 0060): the caption on the brand's dark in its light, the said word in its colour as clean
 * marks it; the last frame on its colour when it has one of its own, else its dark, with the logo on a tile of its
 * light. Drawn on the first colour kept, a logo in that colour vanished, and a warning had to say so (2026-09-29).
 * Under the name, the line the render puts there when a brief has none of its own (`endLine`): it was the town, which
 * no video ever drew (2026-10-12).
 *
 * Both are a video's. Captions come only with a voice and a post has none, and a post does not end on the brand: its
 * words are drawn on its one picture. The preview was called "a photo with its caption", which read as a post
 * (2026-10-12), so it says whose they are.
 */
export function Preview({ colours, logoUrl, photoUrl, name, line }: {
  colours: readonly string[]; logoUrl: string | null; photoUrl: string | null; name: string; line: string;
}) {
  const roles = brandRoles(colours.length ? colours : DEFAULT_COLOURS);
  const card = endCard(roles);
  // The said word, as clean lights it (looks.tsx): the brand's own colour (its light, where the words read through it)
  // where it reads on the dark chip, the chip's words where it would have to become another colour to (a red on a blue
  // went salmon, 2026-09-29).
  const glow = isChromatic(roles.accent) && contrast(roles.ink, mixColour(roles.paper, roles.accent, 0.12)) >= 4.5 ? roles.accent : null;
  const lit = markOn(glow ?? markOn(roles.accent, roles.paper, roles.ink), roles.ink, roles.paper);
  return (
    <figure className="flex flex-col gap-1.5" aria-label="Preview of your look">
      <div className="grid grid-cols-2 gap-2">
        <div className="relative aspect-[4/5] rounded-lg overflow-hidden bg-line flex items-end justify-center p-2.5">
          {photoUrl ? <img src={photoUrl} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <span className="absolute inset-0 grid place-items-center text-faint"><Icon name="image" size={22} /></span>}
          <span className="relative px-2 py-1 rounded-md text-[11px] font-bold" style={{ background: roles.ink, color: roles.paper }} data-testid="preview-caption">
            <span style={{ color: lit }}>New</span> this week
          </span>
        </div>
        <div className="aspect-[4/5] rounded-lg flex flex-col items-center justify-center text-center p-2.5" style={{ background: card.ground, color: card.words }} data-testid="preview-end">
          {logoUrl ? (
            <span className="rounded-md p-1.5 mb-2 max-w-[70%] flex" style={{ background: card.tile }} data-testid="preview-tile">
              <img src={logoUrl} alt="" className="max-w-full max-h-10 object-contain" />
            </span>
          ) : null}
          {/* Broken anywhere rather than run out of the tile: the line is usually a web address, with no space in it. */}
          <span className="text-[12px] font-bold leading-tight max-w-full wrap-anywhere">{name || "Your business"}</span>
          {line ? <span className="text-[10.5px] opacity-85 max-w-full wrap-anywhere" data-testid="preview-line">{line}</span> : null}
        </div>
      </div>
      <figcaption className="text-[11px] text-muted">{colours.length ? "A narrated video's captions over a photo, and a video's last frame. A post has neither." : "The default colours, until you pick some"}</figcaption>
    </figure>
  );
}

/**
 * A few themes to try, then the colours themselves to fine-tune. A theme tapped is the palette, all of it: a theme can
 * be three colours now (a logo's two and its dark, 2026-09-29), and one laid over the front of another left a colour of
 * the last theme behind.
 */
function Themes({ themes, colours, onChange }: { themes: ReturnType<typeof themeProposals>; colours: string[]; onChange: (c: string[]) => void }) {
  const on = (t: { colours: readonly string[] }) => t.colours.join() === colours.join();
  return (
    <Group label="Colour themes">
      <div className="grid grid-cols-2 gap-1.5">
        {themes.map((t) => (
          <button key={t.name} onClick={() => onChange([...t.colours])} aria-pressed={on(t)}
            className={`text-left rounded-lg border-2 bg-panel px-2 py-1.5 ${on(t) ? "border-coral" : "border-line hover:border-line-2"}`}>
            <span className="flex gap-1">{t.colours.map((c) => <span key={c} className="w-4.5 h-4.5 rounded-full border border-black/10" style={{ background: c }} />)}</span>
            <span className="block text-[11.5px] mt-1">{t.name}</span>
          </button>
        ))}
      </div>
      <ColourChips colours={colours} onChange={onChange} />
      <p className="text-[11px] text-muted">Tap a theme to see it, then a colour to fine-tune it.</p>
    </Group>
  );
}

/** Up to three colours: tap one to change it, × to take it off. */
function ColourChips({ colours, onChange }: { colours: string[]; onChange: (c: string[]) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {colours.map((c, i) => (
        <span key={`${c}-${i}`} className="flex items-center gap-1 rounded-full border border-line pl-1 pr-1.5 py-1">
          <label className="w-6 h-6 rounded-full border border-line overflow-hidden cursor-pointer" style={{ background: c }} title="Change this colour">
            <input type="color" value={c} onChange={(e) => onChange(colours.map((x, j) => (j === i ? e.target.value.toLowerCase() : x)))}
              aria-label={`Colour ${i + 1}`} className="opacity-0 w-full h-full cursor-pointer" />
          </label>
          <button onClick={() => onChange(colours.filter((_, j) => j !== i))} aria-label={`Remove ${c}`} className="text-[12px] text-muted hover:text-ink leading-none">×</button>
        </span>
      ))}
      {colours.length < LOOK_COLOURS ? (
        <button onClick={() => onChange([...colours, colours[0] ? "#ffffff" : "#000000"])} className="text-[11.5px] text-muted underline underline-offset-2">add a colour</button>
      ) : null}
    </div>
  );
}

function Pictures({ label, group, keep, onToggle }: { label: string; group: readonly BrandCandidate[]; keep: readonly string[]; onToggle: (id: string) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[12px] font-medium">{label}</span>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(76px,1fr))] gap-1.5">
        {group.map((c) => (
          <button key={c.id} onClick={() => onToggle(c.id)} aria-pressed={keep.includes(c.id)} aria-label={`Picture from ${c.from}`} title={`from ${c.from}`}
            className={`relative rounded-lg overflow-hidden border-2 aspect-square bg-page ${keep.includes(c.id) ? "border-coral" : "border-transparent opacity-70 hover:opacity-100"}`}>
            <img src={thumb(c.id)} alt="" loading="lazy" className="w-full h-full object-cover" />
            {keep.includes(c.id) ? <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-coral text-white grid place-items-center"><Icon name="check" size={12} /></span> : null}
            {c.source === "web" ? <span className="absolute inset-x-0 bottom-0 px-1 py-0.5 bg-gradient-to-t from-black/70 to-transparent text-white text-[9.5px] truncate">{c.from}</span> : null}
          </button>
        ))}
      </div>
    </div>
  );
}
